import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import CategoryCard from '../../component/ConfigurationsPage/CategoryCard.jsx';
import './ConfigurationProductDisplay.css';
import ConfigurationProductDisplayCatDetails from './child/ConfigurationProductDisplayCatDetails.jsx';
import axios from '../../axiosConfig';
import EditImgModel from '../Locations/LocationDetails/EditImgModal.jsx';
import DefaultImg from '../../assets/img/default-restaurant-img.svg';
import TimeSettingPopup from './child/TimeSettingPopup.jsx'; // Import the new component
import ClockIcon from './child/ClockIcon.jsx'; // Import the ClockIcon component

const ConfigurationProductDisplay = ({ products, productCategories, fetchLatestDataAndMap, syncToPos }) => {
  const [activeCard, setActiveCard] = useState(null);
  const [view, setView] = useState('categories');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [categories, setCategories] = useState(productCategories);
  const [subCategories, setSubCategories] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [productToRemove, setProductToRemove] = useState(null);

  // New state for time setting popup
  const [timePopupOpen, setTimePopupOpen] = useState(false);
  const [selectedItemForTime, setSelectedItemForTime] = useState(null);
  const [itemAvailability, setItemAvailability] = useState({});

  const ebrizaLinkedId = localStorage.getItem('ebrizaLinkedId');
  const [coverImgOpen, setCoverImgOpen] = useState(false);
  const [profileImgOpen, setProfileImgOpen] = useState(false);
  const [showProfileImage, setShowProfileImage] = useState(DefaultImg);
  const cardRefs = useRef([]);

  const clientId = localStorage.getItem('partnerId');

  useEffect(() => {
    if (products && productCategories) {
      setCategories(productCategories);
    }
  }, [products, productCategories]);

  // Fetch availability data for categories
  useEffect(() => {
    const fetchAvailability = async () => {
      if (view == 'details') {
        try {
          const availabilityMap = {};

          // Collect all subcategory IDs and types for bulk fetch
          const productIds = [];
          products.forEach(prod => {
            if (prod.ProductId) {
              productIds.push(prod.ProductId.toString());
            }
          });

          if (productIds.length > 0) {
            const itemTypes = Array(productIds.length).fill('product');

            // Use bulk API for subcategories
            const response = await axios.get('/partners/item_availability/bulk', {
              params: {
                client_id: clientId,
                item_ids: productIds.join(','),
                item_types: itemTypes.join(',') // Creates "subcat,subcat,subcat..."
              }
            });

            if (response.data.data && response.data.data.length > 0) {
              response.data.data.forEach(item => {
                availabilityMap[item.item_id] = item.available_time;
              });
            }
          }

          setItemAvailability(availabilityMap);
        } catch (error) {
          console.error('Error fetching bulk availability for subcategories:', error);
        }
      } else {

        if (subCategories.length > 0) {
          try {
            const availabilityMap = {};

            // Collect all subcategory IDs and types for bulk fetch
            const subcatIds = [];
            subCategories.forEach(category => {
              if (category.CategoryId) {
                subcatIds.push(category.CategoryId.toString());
              }
            });

            if (subcatIds.length > 0) {
              const itemTypes = Array(subcatIds.length).fill('subcat');

              // Use bulk API for subcategories
              const response = await axios.get('/partners/item_availability/bulk', {
                params: {
                  client_id: clientId,
                  item_ids: subcatIds.join(','),
                  item_types: itemTypes.join(',') // Creates "subcat,subcat,subcat..."
                }
              });

              if (response.data.data && response.data.data.length > 0) {
                response.data.data.forEach(item => {
                  availabilityMap[item.item_id] = item.available_time;
                });
              }
            }

            setItemAvailability(availabilityMap);
          } catch (error) {
            console.error('Error fetching bulk availability for subcategories:', error);

            // Fallback to individual calls if bulk fails
            const availabilityMap = {};
            for (const category of subCategories) {
              try {

                const response = await axios.get('/partners/item_availability', {
                  params: {
                    client_id: clientId,
                    item_id: category.CategoryId,
                    item_type: 'subcat'
                  }
                });

                if (response.data.data && response.data.data.length > 0) {
                  availabilityMap[category.CategoryId] = response.data.data[0].available_time;
                }
              } catch (individualError) {
                console.error(`Error fetching availability for subcategory ${category.CategoryId}:`, individualError);
              }
            }
            setItemAvailability(availabilityMap);
          }
        }
        else if (categories.length > 0) {
          try {
            const availabilityMap = {};

            // Collect all category IDs and types for bulk fetch
            const catIds = [];
            categories.forEach(category => {
              if (category.CategoryId) {
                catIds.push(category.CategoryId.toString());
              }
            });

            if (catIds.length > 0) {
              const itemTypes = Array(catIds.length).fill('cat');

              // Use bulk API for categories
              const response = await axios.get('/partners/item_availability/bulk', {
                params: {
                  client_id: clientId,
                  item_ids: catIds.join(','),
                  item_types: itemTypes.join(',') // Creates "cat,cat,cat..."
                }
              });

              if (response.data.data && response.data.data.length > 0) {
                response.data.data.forEach(item => {
                  availabilityMap[item.item_id] = item.available_time;
                });
              }
            }

            setItemAvailability(availabilityMap);
          } catch (error) {
            console.error('Error fetching bulk availability for categories:', error);

            // Fallback to individual calls if bulk fails
            const availabilityMap = {};
            for (const category of categories) {
              try {
                const response = await axios.get('/partners/item_availability', {
                  params: {
                    client_id: clientId,
                    item_id: category.CategoryId,
                    item_type: 'cat'
                  }
                });

                if (response.data.data && response.data.data.length > 0) {
                  availabilityMap[category.CategoryId] = response.data.data[0].available_time;
                }
              } catch (individualError) {
                console.error(`Error fetching availability for category ${category.CategoryId}:`, individualError);
              }
            }
            setItemAvailability(availabilityMap);
          }
        }
      }
    };

    fetchAvailability();
  }, [categories, subCategories, view]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeCard !== null && !cardRefs.current[activeCard]?.contains(event.target)) {
        setActiveCard(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeCard]);

  const handleEditImage = async (uploadedUrl) => {
    setCoverImgOpen(false);
    setProfileImgOpen(false);
    if (uploadedUrl) {
      console.log("uploadedUrl cat..", uploadedUrl);
      let payload = {
        'CategoryId': selectedCategoryId,
        'imageUrl': uploadedUrl
      };
      let addMenuCat = await axios.put(`/partners/menu_categories`, payload);
      if (addMenuCat.data) {
        fetchLatestDataAndMap();
      }
    }
  };

  const editCatImage = (categoryId) => {
    setActiveCard(null);
    setSelectedCategoryId(categoryId);
    setProfileImgOpen(true);
  };

  const handleCardClick = (cardIndex, category) => {
    if (!selectedCategory) {
      setSelectedCategory(category);
    }
    if (category.subCat) {
      setSubCategories(category.subCat);
    } else {
      if (subCategories && subCategories.length > 0) {
        setSelectedSubCategory(category);
      }
      setView('details');
    }
  };

  const handleCardMenuClick = (cardIndex) => {
    setActiveCard(activeCard === cardIndex ? null : cardIndex);
  };

  const handleBreadcrumbClick = (item) => {
    if (item == 'cat') {
      setView('categories');
      setSelectedCategory(null);
      setSelectedSubCategory(null);
      setSubCategories([]);
    } else if (item == 'subCat') {
      setView('categories');
      setSelectedSubCategory(null);
    }
  };

  const handleOpenDialog = () => {
    if (!ebrizaLinkedId) {
      setOpenDialog(true);
      setIsRenaming(false);
      setCategoryName('');
    }
  };

  const handleRenameOpen = (categoryId, currentName) => {
    setActiveCard(null);
    setSelectedCategoryId(categoryId);
    setCategoryName(currentName);
    setIsRenaming(true);
    setOpenDialog(true);
  };

  const handleCatDelete = (categoryId, currentName) => {
    setActiveCard(null);
    setProductToRemove(categoryId);
    setOpenConfirmDialog(true);
  };

  // NEW FUNCTION: Handle clock icon click
  const handleClockClick = (categoryId, itemType = 'cat') => {
    setSelectedItemForTime({
      id: categoryId,
      type: itemType
    });
    setTimePopupOpen(true);
  };

  // NEW FUNCTION: Handle time save success
  const handleTimeSaveSuccess = (savedData) => {
    // Update local availability state
    setItemAvailability(prev => ({
      ...prev,
      [savedData.item_id]: savedData.available_time
    }));

    // Show success message
    console.log('Availability saved successfully');
  };

  async function handleConfirmRemove() {
    console.log("Deleting product:", productToRemove);

    try {
      let addMenuCat = await axios.delete(`/partners/menu_categories`, { data: { 'CategoryId': productToRemove } });
      if (addMenuCat.data) {
        fetchLatestDataAndMap();
      }
    } catch (error) {
      console.log("Failed to delete product", error.message);
    } finally {
      setOpenConfirmDialog(false);
      setProductToRemove(null);
    }
  }

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCategoryName('');
  };

  const handleCategoryNameChange = (event) => {
    setCategoryName(event.target.value);
  };

  const handleAddCategory = async () => {
    try {
      if (categoryName) {
        let payload = {
          'name': categoryName,
          'description': '',
          'imageUrl': '',
          'tileColor': ''
        };
        let addMenuCat = await axios.post(`/partners/menu_categories`, payload);

        if (addMenuCat.data) {
          console.log("addMenuCat.", addMenuCat.data);
          const newCategory = addMenuCat.data.data[0];
          setCategories([...categories, newCategory]);
          handleCloseDialog();
        } else {
          console.error('Failed to add category');
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleRenameCategory = async () => {
    try {
      if (categoryName) {
        let payload = {
          'name': categoryName,
          'CategoryId': selectedCategoryId,
        };
        let updateMenuCat = await axios.put(`/partners/menu_categories`, payload);

        if (updateMenuCat.data.success) {
          console.log("updateMenuCat.", updateMenuCat.data);
          let newData = categories.map(cat => {
            if (cat.id === selectedCategoryId) {
              cat['name'] = categoryName;
            }
            return cat;
          });
          setCategories(newData);
          handleCloseDialog();
        } else {
          console.error('Failed to add category');
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const renderCards = () => {
    let catToShow = subCategories.length > 0 ? subCategories : categories;

    return (
      <>
        <EditImgModel
          coverImgOpen={coverImgOpen}
          profileImgOpen={profileImgOpen}
          locationId=""
          handleClose={handleEditImage}
          setShowProfileImage={setShowProfileImage}
        />

        <div className="cards">
          {catToShow.map((category, index) => (
            <div
              key={index}
              className="category-card-1"
              style={{ cursor: 'pointer' }}
              ref={(el) => (cardRefs.current[index] = el)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <CategoryCard
                  name1={category.name}
                  totalItems={category.totalItems}
                  totalSeen={category.totalActive}
                  imgUrl={category.imageUrl}
                  onCardMenuClick={() => handleCardMenuClick(index)}
                  onCardClick={() => handleCardClick(index, category)}
                />
                <ClockIcon
                  onClick={() => handleClockClick(category.CategoryId, subCategories.length > 0 ? 'subcat' : 'cat')}
                  hasSchedule={itemAvailability[category.CategoryId]?.length > 0}
                  tooltip="Set availability schedule"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: '-3px',
                    left: '-3px',
                    color: 'black'
                  }}
                />
              </div>

              {activeCard === index && !ebrizaLinkedId && (
                <div className="card-actions">
                  <div className="actions2">
                    <div className="option" onClick={() => handleRenameOpen(category.CategoryId, category.name)}>
                      <div className="left">
                        <img className="icons23" alt="" src="/icons-54.svg" />
                        <div className="edit">Rename</div>
                      </div>
                      <img className="icons24" alt="" src="/icons2.svg" />
                    </div>

                    <div className="option2" onClick={() => editCatImage(category.CategoryId)}>
                      <div className="left2">
                        <img className="icons27" alt="" src="/icons-56.svg" />
                        <div className="edit2">Change icon</div>
                      </div>
                      <img className="icons28" alt="" src="/icons2.svg" />
                    </div>

                    <div className="option4" onClick={() => handleCatDelete(category.CategoryId, category.name)}>
                      <div className="left3">
                        <img className="icons29" alt="" src="/icons-58.svg" />
                        <div className="edit3">Delete</div>
                      </div>
                      <img className="icons30" alt="" src="/icons2.svg" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          <div
            onClick={handleOpenDialog}
            style={ebrizaLinkedId ? { cursor: 'not-allowed' } : { cursor: 'pointer' }}
            className="category-card71"
          >
            <img className="icons22" alt="" src="/icons1.svg" />
            <div className="add-category7">Add category</div>
          </div>
        </div>
      </>
    );
  };

  return (
    <section className="top-bar2">
      <div className="inner-content-wrapper">
        <div className="inner-content">
          <div className="content3" style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}>
            <div className="actions1">
              <div className="buttons1">
                <div className="input-dropdown-base">
                  <div className="input-with-label">
                    <FormControl
                      className="input"
                      variant="standard"
                      sx={{
                        borderTopWidth: '1px',
                        borderRightWidth: '1px',
                        borderBottomWidth: '1px',
                        borderLeftWidth: '1px',
                        borderRadius: '8px',
                        width: '100%',
                        height: '44px',
                        m: 0,
                        p: 0,
                        '& .MuiInputBase-root': {
                          m: 0,
                          p: 0,
                          minHeight: '44px',
                          justifyContent: 'center',
                          display: 'inline-flex',
                        },
                        '& .MuiInputLabel-root': {
                          m: 0,
                          p: 0,
                          minHeight: '44px',
                          display: 'inline-flex',
                        },
                        '& .MuiMenuItem-root': {
                          m: 0,
                          p: 0,
                          height: '44px',
                          display: 'inline-flex',
                        },
                        '& .MuiSelect-select': {
                          m: 0,
                          p: 0,
                          height: '44px',
                          alignItems: 'center',
                          display: 'inline-flex',
                        },
                        '& .MuiInput-input': { m: 0, p: 0 },
                        '& .MuiInputBase-input': {
                          color: '#3c3a35',
                          fontSize: 16,
                          fontWeight: 'Semi Bold',
                          fontFamily: 'Inter',
                          textAlign: 'left',
                          p: '0 !important',
                        },
                      }}
                    >
                      <InputLabel color="primary" />
                      <Select
                        color="primary"
                        disableUnderline
                        displayEmpty
                        IconComponent={() => (
                          <img
                            width="20px"
                            height="20px"
                            src="/chevrondown.svg"
                            style={{ marginRight: '3px' }}
                          />
                        )}
                      >
                        <MenuItem>Nomenclator</MenuItem>
                      </Select>
                      <FormHelperText />
                    </FormControl>
                  </div>
                </div>
              </div>
            </div>

            <button
              style={ebrizaLinkedId ? { cursor: 'not-allowed' } : {}}
              className="button-base12"
              onClick={(event) => {
                event.preventDefault();
                syncToPos();
              }}
            >
              <img className="icons12" alt="" src="/icons-11.svg" />
              <div className="text7">Sync to POS</div>
            </button>
          </div>

          <div className="categories">
            <div className="bread-countsactions">
              <div className="bread-counts">
                <a className={view != 'details' ? "active" : "categories1"} onClick={() => handleBreadcrumbClick('cat')}>
                  Categories
                </a>
                {
                  subCategories && subCategories.length > 0 && (
                    <>
                      <span style={{ margin: '0 10px 0 10px' }}> &gt; </span>
                      <a className={view != 'details' ? "active" : "categories1"} onClick={() => handleBreadcrumbClick('subCat')}>
                        {selectedCategory['name']}
                      </a>
                    </>
                  )
                }
                {view === 'details' && (
                  <>
                    {
                      selectedSubCategory ? (
                        <>
                          <span style={{ margin: '0 10px 0 10px' }}> &gt; </span>
                          <a className="active">{selectedSubCategory?.name}</a>
                        </>
                      ) : (
                        <>
                          <span style={{ margin: '0 10px 0 10px' }}> &gt; </span>
                          <a className="active">{selectedCategory?.name}</a>
                        </>
                      )
                    }
                  </>
                )}
              </div>

              <div className="buttons2 sync-button-cat">
                <div style={{ cursor: 'pointer' }} onClick={fetchLatestDataAndMap} className="button-base14">
                  <img className="icons14" alt="" src="/icons-121.svg" />
                </div>
              </div>
            </div>

            {view === 'categories' ? renderCards() :
              <ConfigurationProductDisplayCatDetails
                selectedCategory={selectedSubCategory ? selectedSubCategory : selectedCategory}
                products={products}
                allCatWithProducts={productCategories}
                fetchLatestDataAndMap={fetchLatestDataAndMap}
                onClockClick={handleClockClick} // Pass clock handler to details
                itemAvailability={itemAvailability}
              />
            }
          </div>
        </div>
      </div>

      {/* Time Setting Popup */}
      <TimeSettingPopup
        open={timePopupOpen}
        onClose={() => setTimePopupOpen(false)}
        itemId={selectedItemForTime?.id}
        itemType={selectedItemForTime?.type || 'cat'}
        currentAvailability={selectedItemForTime?.id ? itemAvailability[selectedItemForTime.id] : []}
        onSaveSuccess={handleTimeSaveSuccess}
        clientId={clientId}
      />

      {/* Add/Rename Category Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{isRenaming ? 'Rename Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="categoryName"
            label="Category Name"
            type="text"
            fullWidth
            variant="standard"
            value={categoryName}
            onChange={handleCategoryNameChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={isRenaming ? handleRenameCategory : handleAddCategory}>
            {isRenaming ? 'Rename' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this category?</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmRemove} color="primary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default ConfigurationProductDisplay;